import type {
  AcceptSubmission,
  FirstView,
  IntakeDryRunResult,
  IntakeLink,
  IntakeLinkInput,
  IntakeLinkPatch,
  IntakeSubmission,
  IntakeTier,
  PublicIntakeLink,
  SubmissionStatus,
  TierRule,
  TriageAnswers,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { refreshLists } from '../../lib/list';

/** Submission links and submissions (task 68). */

const linksKey = (t?: string) => ['intake-links', t] as const;
const subsKey = (t?: string) => ['intake-submissions', t] as const;

export function useIntakeLinks() {
  const { tenant } = useSession();
  return useQuery({ queryKey: linksKey(tenant?.id), queryFn: () => api<IntakeLink[]>('GET', '/intake/links'), enabled: !!tenant }); // prettier-ignore
}

export function useIntakeLinkChanges() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const done = () => qc.invalidateQueries({ queryKey: linksKey(tenant?.id) });
  return {
    create: useMutation({ mutationFn: (body: IntakeLinkInput) => api<IntakeLink>('POST', '/intake/links', body), onSuccess: done }), // prettier-ignore
    update: useMutation({ mutationFn: ({ id, ...body }: IntakeLinkPatch & { id: string }) => api<IntakeLink>('PATCH', `/intake/links/${id}`, body), onSuccess: done }), // prettier-ignore
  };
}

export const dryRun = (id: string, rules: TierRule[], default_tier: IntakeTier) =>
  api<IntakeDryRunResult>('POST', `/intake/links/${id}/dry-run`, { rules, default_tier });

export function useSubmissions(status: SubmissionStatus | '') {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...subsKey(tenant?.id), status],
    queryFn: () => api<IntakeSubmission[]>('GET', `/intake/submissions${status ? `?status=${status}` : ''}`),
    enabled: !!tenant,
  });
}

export function useDecide() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const done = () => {
    void qc.invalidateQueries({ queryKey: subsKey(tenant?.id) });
    void qc.invalidateQueries({ queryKey: linksKey(tenant?.id) });
    void refreshLists(qc);
  };
  return {
    accept: useMutation({ mutationFn: ({ id, ...body }: AcceptSubmission & { id: string }) => api<IntakeSubmission>('POST', `/intake/submissions/${id}/accept`, body), onSuccess: done }), // prettier-ignore
    reject: useMutation({ mutationFn: ({ id, note }: { id: string; note: string }) => api<IntakeSubmission>('POST', `/intake/submissions/${id}/reject`, { note }), onSuccess: done }), // prettier-ignore
  };
}

export const publicLink = (token: string) => api<PublicIntakeLink>('GET', `/public/intake/${token}`);
export const submitIntake = (
  token: string,
  answers: TriageAnswers,
  company_fax: string,
  check_domain: boolean,
) =>
  // prettier-ignore
  api<{ received: true }>('POST', `/public/intake/${token}`, {
    answers,
    ...(check_domain ? { check_domain } : {}),
    ...(company_fax ? { company_fax } : {}),
  });

/** A submission's first outside-in view (task 73), when the business agreed to it on the form. */
export function useFirstView(id: string, enabled: boolean) {
  const { tenant } = useSession();
  return useQuery({ queryKey: ['first-view', tenant?.id, id], queryFn: () => api<FirstView>('GET', `/intake/submissions/${id}/first-view`), enabled: !!tenant && enabled }); // prettier-ignore
}

export function useCheckFirstView(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({ mutationFn: () => api<FirstView>('POST', `/intake/submissions/${id}/first-view`), onSuccess: (v) => qc.setQueryData(['first-view', tenant?.id, id], v) }); // prettier-ignore
}
